import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import { createContext, use, useCallback, useMemo, useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import { useLocale } from './locale';

/**
 * A short confirmation after a save (UX-014): what happened, in words, with a link to the record when
 * there is one. Announced politely to assistive technology; it never replaces the page's own state,
 * which already shows the saved values.
 */
interface ToastMessage {
  id: number;
  message: string;
  severity: 'success' | 'info';
  link?: { to: string; label: string } | undefined;
}

interface ToastApi {
  success: (message: string, link?: { to: string; label: string }) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const { t } = useLocale();
  const [current, setCurrent] = useState<ToastMessage | undefined>();
  const show = useCallback(
    (message: string, severity: ToastMessage['severity'], link?: ToastMessage['link']) =>
      setCurrent({ id: Date.now(), message, severity, link }),
    [],
  );
  const api = useMemo<ToastApi>(
    () => ({
      success: (message, link) => show(message, 'success', link),
      info: (message) => show(message, 'info'),
    }),
    [show],
  );
  return (
    <ToastContext value={api}>
      {children}
      <Snackbar
        key={current?.id}
        open={!!current}
        autoHideDuration={6000}
        onClose={(_event, reason) => {
          if (reason !== 'clickaway') setCurrent(undefined);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={current?.severity ?? 'success'}
          variant="filled"
          role="status"
          onClose={() => setCurrent(undefined)}
          closeText={t('actions.close')}
          action={
            current?.link ? (
              <Button
                component={RouterLink}
                to={current.link.to}
                color="inherit"
                size="small"
                onClick={() => setCurrent(undefined)}
              >
                {current.link.label}
              </Button>
            ) : undefined
          }
          sx={{ alignItems: 'center' }}
        >
          {current?.message}
        </Alert>
      </Snackbar>
    </ToastContext>
  );
}

/** The confirmation toast; outside the provider (a test harness) it does nothing. */
export function useToast(): ToastApi {
  return use(ToastContext) ?? NO_TOAST;
}

const NO_TOAST: ToastApi = { success: () => undefined, info: () => undefined };
